import type { PointerEvent, ReactNode } from 'react'
import type { BookLevel } from '@/lib/market/live'

import { useState } from 'react'
import { useElementWidth } from '@/hooks/use-element-width'
import { scaleLinear } from '@/components/charts/scales'
import { formatCompact, formatPrice } from '@/lib/market/format'

interface DepthChartProps {
    bids: BookLevel[]
    asks: BookLevel[]
    height?: number
}

const PAD = 8
const BOTTOM = 20

const DepthChart = ({ bids, asks, height = 150 }: DepthChartProps): ReactNode => {
    const [ref, width] = useElementWidth<HTMLDivElement>(320)
    const [hover, setHover] = useState<{ side: 'bid' | 'ask', level: BookLevel } | null>(null)

    if (!bids.length || !asks.length) return null

    const minPrice = bids[bids.length - 1].price
    const maxPrice = asks[asks.length - 1].price
    const maxTotal = Math.max(bids[bids.length - 1].total, asks[asks.length - 1].total, 1)
    const plotHeight = height - PAD - BOTTOM
    const x = scaleLinear([minPrice, maxPrice], [PAD, width - PAD])
    const y = scaleLinear([0, maxTotal * 1.08], [PAD + plotHeight, PAD])
    const base = y(0)
    const mid = (bids[0].price + asks[0].price) / 2

    let bidPath = `M${x(mid)},${base}L${x(bids[0].price)},${base}`
    bids.forEach((level, index) => {
        bidPath += `L${x(level.price)},${y(level.total)}`
        const next = bids[index + 1]
        if (next) bidPath += `L${x(next.price)},${y(level.total)}`
    })
    bidPath += `L${x(minPrice)},${base}Z`

    let askPath = `M${x(mid)},${base}L${x(asks[0].price)},${base}`
    asks.forEach((level, index) => {
        askPath += `L${x(level.price)},${y(level.total)}`
        const next = asks[index + 1]
        if (next) askPath += `L${x(next.price)},${y(level.total)}`
    })
    askPath += `L${x(maxPrice)},${base}Z`

    const handleMove = (event: PointerEvent<SVGRectElement>) => {
        const svg = event.currentTarget.ownerSVGElement
        if (!svg) return

        const bounds = svg.getBoundingClientRect()
        const px = (event.clientX - bounds.left) * (width / Math.max(1, bounds.width))
        const all = [
            ...bids.map(level => ({ side: 'bid' as const, level })),
            ...asks.map(level => ({ side: 'ask' as const, level }))
        ]
        let best = all[0]
        let distance = Infinity

        for (const entry of all) {
            const gap = Math.abs(x(entry.level.price) - px)
            if (gap < distance) {
                distance = gap
                best = entry
            }
        }

        setHover(best)
    }

    return (
        <div ref={ref} className='relative w-full' role='img' aria-label={`Order book depth: ${formatCompact(bids[bids.length - 1].total)} shares bid down to ${formatPrice(minPrice)}, ${formatCompact(asks[asks.length - 1].total)} shares offered up to ${formatPrice(maxPrice)}`}>
            <svg width='100%' height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio='xMinYMin meet' aria-hidden='true' className='block'>
                <path d={bidPath} fill='var(--up)' fillOpacity={0.14} stroke='var(--up)' strokeWidth={1.5} strokeLinejoin='round' />
                <path d={askPath} fill='var(--down)' fillOpacity={0.14} stroke='var(--down)' strokeWidth={1.5} strokeLinejoin='round' />
                <line x1={x(mid)} x2={x(mid)} y1={PAD} y2={base} stroke='var(--muted-foreground)' strokeOpacity={0.5} strokeDasharray='3 3' />
                <line x1={PAD} x2={width - PAD} y1={base} y2={base} stroke='var(--axis)' shapeRendering='crispEdges' />
                <text x={PAD} y={height - 5} fontSize={10.5} fill='var(--muted-foreground)'>{formatPrice(minPrice)}</text>
                <text x={x(mid)} y={height - 5} fontSize={10.5} textAnchor='middle' fill='var(--muted-foreground)'>{formatPrice(mid)}</text>
                <text x={width - PAD} y={height - 5} fontSize={10.5} textAnchor='end' fill='var(--muted-foreground)'>{formatPrice(maxPrice)}</text>

                {hover && (
                    <circle cx={x(hover.level.price)} cy={y(hover.level.total)} r={4} fill={hover.side === 'bid' ? 'var(--up)' : 'var(--down)'} stroke='var(--surface)' strokeWidth={2} />
                )}

                <rect x={PAD} y={PAD} width={Math.max(0, width - PAD * 2)} height={plotHeight} fill='transparent' onPointerMove={handleMove} onPointerLeave={() => setHover(null)} />
            </svg>

            {hover && (
                <div className='pointer-events-none absolute right-2 top-1 rounded-md border border-border bg-popover/95 px-2 py-1 text-[11px] shadow-lg'>
                    <span className={hover.side === 'bid' ? 'text-up' : 'text-down'}>{hover.side === 'bid' ? 'Bids' : 'Offers'}</span>
                    <span className='text-muted-foreground'> to {formatPrice(hover.level.price)}: </span>
                    <span className='font-medium tabular-nums text-foreground'>{formatCompact(hover.level.total)} shares</span>
                </div>
            )}
        </div>
    )
}

export default DepthChart
